import type { OrderLeg } from "../optionLegs/optionLeg.types";
import { MAX_TICKET_LEGS } from "../optionLegs/optionLeg.types";
import type { WorkspaceDraft } from "./groups.slice";
import { adoptPopulatedGroupSymbol } from "./groups.slice";
import type {
  GroupId,
  OrderTicketState,
  RouteOptionContractInput,
  TicketsSlice,
  WorkspaceSliceCreator,
  WorkspaceWidget,
} from "./workspace.types";
import { normalizeWorkspaceSymbol } from "./workspace.utils";

// ---------------------------------------------------------------------------
// Minimal state shape for the routing selector
// ---------------------------------------------------------------------------

/**
 * The subset of workspace state that `selectOptionTargetTicketId` reads.
 * Uses the proper `WorkspaceWidget` field types (not `kind: string`) so that
 * the selector stays type-safe without casting.
 */
type TicketRoutingState = {
  widgets: Record<string, Pick<WorkspaceWidget, "kind" | "groupId">>;
  tickets: Record<
    string,
    Pick<OrderTicketState, "lastInteractedAt" | "createdOrder">
  >;
};

type Candidate = {
  id: string;
  lastInteractedAt: number;
  createdOrder: number;
};

// ---------------------------------------------------------------------------
// Selector
// ---------------------------------------------------------------------------

/**
 * Resolves the target order-ticket widget ID for an option-leg routing request.
 *
 * Priority tiers are preserved even when no candidate has been interacted with:
 * 1. Same-group pool, interacted — highest `lastInteractedAt`;
 *    tie-break: smallest `createdOrder`
 * 2. Same-group pool, un-interacted — smallest `createdOrder`
 * 3. Unassigned pool, interacted — highest `lastInteractedAt`;
 *    tie-break: smallest `createdOrder`
 * 4. Unassigned pool, un-interacted — smallest `createdOrder`
 * 5. `undefined` — no eligible candidate
 *
 * Tickets assigned to a *different* group are never eligible.
 */
export const selectOptionTargetTicketId = (
  state: TicketRoutingState,
  sourceGroupId: GroupId,
): string | undefined => {
  const sameGroup: Candidate[] = [];
  const unassigned: Candidate[] = [];

  for (const [id, widget] of Object.entries(state.widgets)) {
    if (widget.kind !== "orderTicket") {
      continue;
    }
    const gid = widget.groupId;
    // Exclude tickets assigned to a different group.
    if (gid !== null && gid !== sourceGroupId) {
      continue;
    }
    const ticket = state.tickets[id];
    if (!ticket) {
      continue;
    }
    const c: Candidate = {
      id,
      lastInteractedAt: ticket.lastInteractedAt,
      createdOrder: ticket.createdOrder,
    };
    if (gid === sourceGroupId) {
      sameGroup.push(c);
    } else {
      unassigned.push(c);
    }
  }

  // Priority 1+2: same-group pool is exhausted before any unassigned candidate.
  const sgInteracted = sameGroup.filter((c) => c.lastInteractedAt > 0);
  if (sgInteracted.length > 0) {
    sgInteracted.sort(
      (a, b) =>
        b.lastInteractedAt - a.lastInteractedAt ||
        a.createdOrder - b.createdOrder,
    );
    return sgInteracted[0].id;
  }
  if (sameGroup.length > 0) {
    sameGroup.sort((a, b) => a.createdOrder - b.createdOrder);
    return sameGroup[0].id;
  }

  // Priority 3+4: unassigned pool.
  const uaInteracted = unassigned.filter((c) => c.lastInteractedAt > 0);
  if (uaInteracted.length > 0) {
    uaInteracted.sort(
      (a, b) =>
        b.lastInteractedAt - a.lastInteractedAt ||
        a.createdOrder - b.createdOrder,
    );
    return uaInteracted[0].id;
  }
  if (unassigned.length > 0) {
    unassigned.sort((a, b) => a.createdOrder - b.createdOrder);
    return unassigned[0].id;
  }

  return undefined;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Mutates `ticket.legs` in-place (Immer-safe):
 * - Same contract + same `quoteSide` → remove (toggle off).
 * - Same contract + different `quoteSide` → swap (price + side update).
 * - New contract and capacity available → append (up to `MAX_TICKET_LEGS`).
 * - New contract at cap → no-op.
 */
const toggleLeg = (ticket: OrderTicketState, leg: OrderLeg): void => {
  const idx = ticket.legs.findIndex(
    (l) => l.contract.symbol === leg.contract.symbol,
  );
  if (idx >= 0) {
    if (ticket.legs[idx].quoteSide === leg.quoteSide) {
      ticket.legs.splice(idx, 1);
    } else {
      ticket.legs[idx] = leg;
    }
  } else if (ticket.legs.length < MAX_TICKET_LEGS) {
    ticket.legs.push(leg);
  }
};

/**
 * Assigns an unassigned widget to `groupId` and runs the same join
 * reconciliation that `assignWidgetGroup` uses for a populated destination:
 * updates `localSymbol` and clears foreign-underlying legs.
 * No-op when the widget is already assigned to any group.
 */
const applyUnassignedJoin = (
  draft: WorkspaceDraft,
  widgetId: string,
  groupId: GroupId,
): void => {
  const widget = draft.widgets[widgetId];
  if (!widget || widget.groupId !== null) {
    return;
  }
  widget.groupId = groupId;
  const gs = draft.groups[groupId]?.linkState.symbol;
  if (gs) {
    adoptPopulatedGroupSymbol(draft, widgetId, widget, gs);
  }
};

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createTicketRoutingSlice: WorkspaceSliceCreator<TicketsSlice> = (
  set,
) => ({
  tickets: {},
  nextTicketCreationOrder: 0,

  // -------------------------------------------------------------------------
  // Step 3: one-transaction option routing
  // -------------------------------------------------------------------------

  routeOptionContract: (input: RouteOptionContractInput): string => {
    const {
      sourceGroupId,
      sourceSymbol,
      contract,
      price,
      quoteSide,
      newTicket,
    } = input;
    const contractUnderlying = normalizeWorkspaceSymbol(
      contract.underlying_symbol,
    );

    // Guard: the source widget is authoritative for its own clicks, so the
    // contract is checked against the symbol that widget was displaying rather
    // than against its group's. A contract from any other symbol belongs to a
    // view the widget has already left, and the entire action is a true no-op —
    // `set()` is never called, no state is mutated, and no subscription
    // notification fires.
    if (normalizeWorkspaceSymbol(sourceSymbol) !== contractUnderlying) {
      return "";
    }

    let resolvedId = "";

    set((draft) => {
      // Cast to WorkspaceDraft for typed helper calls. This is a structural
      // assertion — WritableDraft<WorkspaceStore> satisfies the shape.
      const d = draft as WorkspaceDraft;

      // Step 1: resolve same-group target, then unassigned target.
      const targetId = selectOptionTargetTicketId(draft, sourceGroupId);

      let effectiveId: string;

      if (targetId === undefined) {
        // Step 2: no eligible candidate — insert newTicket unless the ID
        // already exists (guard against clobbering an existing widget).
        // On collision resolvedId stays "" (no-target sentinel).
        const { id, placement } = newTicket;
        if (draft.widgets[id]) {
          return;
        }
        draft.widgets[id] = {
          id,
          kind: "orderTicket",
          groupId: sourceGroupId,
        };
        // Normalize placement.widgetId to the ticket ID regardless of what
        // the caller supplied.
        draft.placements[id] = { ...placement, widgetId: id };
        const order = draft.nextTicketCreationOrder;
        draft.tickets[id] = {
          localSymbol: contractUnderlying,
          legs: [],
          lastInteractedAt: 0,
          createdOrder: order,
        };
        draft.nextTicketCreationOrder += 1;
        effectiveId = id;
      } else {
        effectiveId = targetId;

        // Step 3: if unassigned, assign to source group and run join
        // reconciliation (same semantics as `assignWidgetGroup`).
        applyUnassignedJoin(d, effectiveId, sourceGroupId);
      }

      // Step 4: toggle the leg and stamp interaction time. A ticket holds one
      // underlying at a time, so legs from another one are dropped rather than
      // combined into an order that spans two symbols.
      const ticket = draft.tickets[effectiveId];
      if (ticket) {
        ticket.legs = ticket.legs.filter(
          (leg) => leg.contract.underlying_symbol === contractUnderlying,
        );
        toggleLeg(ticket, { contract, price, quoteSide });
        ticket.lastInteractedAt = Date.now();
      }

      resolvedId = effectiveId;
    });

    return resolvedId;
  },

  // -------------------------------------------------------------------------
  // Step 4: symbol publication without isOverridden / resetToGroup
  // -------------------------------------------------------------------------

  setTicketSymbol: (id: string, symbol: string): void => {
    set((draft) => {
      const widget = draft.widgets[id];
      const ticket = draft.tickets[id];
      if (!widget || !ticket) {
        return;
      }

      const normalized = normalizeWorkspaceSymbol(symbol);

      if (widget.groupId !== null) {
        return;
      }
      // Ungrouped ticket: compare previous effective underlying; clear only
      // when it changes.
      const prevUnderlying = ticket.localSymbol;
      ticket.localSymbol = normalized;
      if (prevUnderlying !== normalized) {
        ticket.legs = ticket.legs.filter(
          (leg) => leg.contract.underlying_symbol === normalized,
        );
      }
    });
  },

  touchTicket: (id: string): void => {
    set((draft) => {
      const ticket = draft.tickets[id];
      if (ticket) {
        ticket.lastInteractedAt = Date.now();
      }
    });
  },

  removeTicketLeg: (ticketId: string, legIndex: number): void => {
    set((draft) => {
      const ticket = draft.tickets[ticketId];
      if (ticket && legIndex >= 0 && legIndex < ticket.legs.length) {
        ticket.legs.splice(legIndex, 1);
      }
    });
  },

  updateTicketLegQuoteSide: (ticketId, contractSymbol, quoteSide): void => {
    set((draft) => {
      const ticket = draft.tickets[ticketId];
      const leg = ticket?.legs.find(
        (entry) => entry.contract.symbol === contractSymbol,
      );
      if (!leg || (quoteSide !== "bid" && quoteSide !== "ask")) {
        return;
      }
      leg.quoteSide = quoteSide;
    });
  },

  clearTicketLegs: (ticketId: string): void => {
    set((draft) => {
      const ticket = draft.tickets[ticketId];
      if (ticket && ticket.legs.length > 0) {
        ticket.legs = [];
      }
    });
  },

  setTicketDraft: (ticketId, nextDraft): void => {
    set((draft) => {
      const ticket = draft.tickets[ticketId];
      if (!ticket) {
        return;
      }
      if (nextDraft === undefined) {
        delete ticket.draft;
        return;
      }
      ticket.draft = {
        side: nextDraft.side,
        quantity: nextDraft.quantity,
        ...(nextDraft.positionIntent
          ? { positionIntent: nextDraft.positionIntent }
          : {}),
      };
      ticket.lastInteractedAt = Date.now();
    });
  },
});
